import { confirmPhotoSubmission } from './photoSubmission'
import { ChangeEvent, FormEvent, Fragment, useEffect, useRef, useState } from 'react'
import {
  playBoardEventSound,
  playBoardGoalSound,
  playBoardStepSound,
  playTreasureOpenSound,
  prepareRewardSound,
  shouldPlayTreasureSound,
} from './sounds'

type Role = 'parent' | 'child'
type User = { id: string; name: string; role: Role }
type ParentView = 'dashboard' | 'today' | 'schedule' | 'rewards' | 'history' | 'photos' | 'settings'
type Bootstrap = { setupRequired: boolean; users: User[] }
type DailyTask = { id: string; taskName: string; sortOrder: number; completedAt: string | null }
type TreasureState = {
  state: 'locked' | 'ready' | 'opened'
  moveCount: number | null
  drawnAt: string | null
  approvalSequence: number | null
}
type NextRollEffect = 'none' | 'half' | 'minimum_3' | 'minimum_4' | 'double'
type AppliedRollEffect = NextRollEffect | 'half_blocked'
type BoardEventType = 'none' | 'add_1' | 'add_2' | 'add_3' | 'half_next' | 'minimum_3_next' | 'barrier' | 'sparkle' | 'lucky' | 'add_5' | 'minimum_4_next' | 'double_next' | 'extra_roll'
type BoardEvent = {
  position: number
  type: Exclude<BoardEventType, 'none'>
  label: string
  icon: string
}
type BoardMovement = {
  fromPosition: number
  requestedMoveCount: number
  effectiveMoveCount: number
  appliedRollEffect: AppliedRollEffect
  barrierConsumed: boolean
  baseToPosition: number
  eventType: BoardEventType
  eventMoveCount: number
  extraRollValue: number | null
  eventMessage: string | null
  appliedMoveCount: number
  toPosition: number
  reachedGoal: boolean
  boardVersion: number
  boardGoal: number
  nextRollEffectAfter: NextRollEffect
  hasBarrierAfter: boolean
  movedAt: string
  lapNumber: number
}
type BoardState = {
  currentPosition: number
  goal: number
  boardVersion: number
  reachedGoal: boolean
  completedLaps: number
  currentLap: number
  nextRollEffect: NextRollEffect
  hasBarrier: boolean
  events: BoardEvent[]
  canAdvance: boolean
  startsNextLap: boolean
  todayMovement: BoardMovement | null
}
type BoardCelebration = {
  kind: 'event' | 'goal'
  icon: string
  title: string
  message: string
}
type RewardTicket = {
  id: string
  childId: string
  rewardId: string
  rewardName: string
  sourceGoalCount: number
  sourceBoardMovementId: string | null
  acquiredAt: string
  status: 'available' | 'pending_use' | 'used'
  usedAt: string | null
}
type DailyStudy = {
  studyDate: string
  taskRevision: number
  child: { id: string; name: string }
  status: 'not_started' | 'pending' | 'approved' | 'rejected' | 'holiday' | 'expired'
  isRestDay: boolean
  restReason: string | null
  restSetAt: string | null
  submittedAt: string | null
  approvedAt: string | null
  lotteryUnlocked: boolean
  treasure: TreasureState
  board: BoardState
  rewardTickets: RewardTicket[]
  goalRewardTicket: RewardTicket | null
  taskSource: 'schedule' | 'override' | 'none'
  tasks: DailyTask[]
  progress: { completed: number; total: number }
  photoSubmissionEnabled: boolean
  photoSubmissionState: 'disabled' | 'locked' | 'required' | 'pending' | 'approved' | 'retry_requested'
  photoSubmission: null | {
    id: string; attemptNumber: number; taskRevision: number; status: 'pending' | 'approved' | 'retry_requested' | 'expired'
    imageCount: number; submittedAt: string; reviewedAt: string | null
  }
}
type ParentStudy = { studyDate: string; children: DailyStudy[] }
type ScheduleTask = { id: string; childId: string; dayOfWeek: number; taskName: string; sortOrder: number }
type ChildSchedule = {
  child: { id: string; name: string }
  days: Array<{ dayOfWeek: number; tasks: ScheduleTask[] }>
}
type SchedulePayload = { children: ChildSchedule[] }
type ChildPlanDay = {
  date: string
  dayOfWeek: number
  isToday: boolean
  isRestDay: boolean
  restReason: string | null
  taskSource: 'rest' | 'override' | 'snapshot' | 'schedule' | 'none'
  tasks: Array<{ taskName: string; sortOrder: number }>
}
type ChildWeekPlan = {
  range: { from: string; to: string; days: number }
  days: ChildPlanDay[]
}
type RewardCandidate = { id: string; name: string; selectedForNextGoal: boolean }
type RewardChild = {
  child: { id: string; name: string }
  rewards: RewardCandidate[]
  tickets: RewardTicket[]
  board: {
    currentPosition: number
    goal: number
    boardVersion: number
    completedLaps: number
    currentLap: number
    canStartNextLap: boolean
  }
}
type RewardPayload = { children: RewardChild[] }
type RestDay = { restDate: string; reason: string | null; setAt: string }
type DashboardChild = DailyStudy & {
  todayStatus: 'rest' | 'goal' | 'board_moved' | 'treasure_opened' | 'approved' | 'photo_pending' | 'photo_required' | 'pending' | 'studying' | 'not_started'
  todayMoveCount: number
  availableRewardTicketCount: number
  upcomingRestDays: RestDay[]
}
type DashboardPayload = { studyDate: string; children: DashboardChild[] }
type HistoryDay = {
  studyDate: string; isRestDay: boolean; restReason: string | null; status: string
  submittedAt: string | null; approvedAt: string | null; taskRevision: number; approvalSequence: number
  tasks: Array<{ taskName: string; completedAt: string | null }>
  progress: { completed: number; total: number }; approved: boolean
  treasure: { moveCount: number; drawnAt: string } | null
  movement: BoardMovement | null
  rewardTicket: { id: string; rewardName: string; acquiredAt: string; status: string; usedAt: string | null } | null
  photoSubmission: { submissionCount: number; attempts: number; finalStatus: string; approvedAt: string | null } | null
  restart: { allowed: boolean; reason: 'available' | 'treasure_opened' | 'not_approved' }
}
type HistoryPayload = { child: { id: string; name: string }; range: { from: string; to: string; days: number }; days: HistoryDay[] }
type PhotoAdminPayload = {
  settings: Array<{ childId: string; childName: string; photoSubmissionEnabled: boolean }>
  pending: Array<{
    id: string; childId: string; childName: string; studyDate: string; attemptNumber: number
    imageCount: number; submittedAt: string
    images: Array<{ id: string; sortOrder: number; mimeType: string }>
    tasks: Array<{ taskName: string; completedAt: string | null }>
  }>
  cleanup: { deleteFailedCount: number }
}
type SelectedPhoto = { id: string; url: string; name: string; file: File }

const MAX_PHOTO_COUNT = 10
const MAX_PHOTO_BYTES = 2_000_000
const MAX_PHOTO_TOTAL_BYTES = 20_000_000

const WEEKDAYS = [
  { value: 1, short: '月', label: '月曜日' },
  { value: 2, short: '火', label: '火曜日' },
  { value: 3, short: '水', label: '水曜日' },
  { value: 4, short: '木', label: '木曜日' },
  { value: 5, short: '金', label: '金曜日' },
  { value: 6, short: '土', label: '土曜日' },
  { value: 0, short: '日', label: '日曜日' },
]

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

const api = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  return (await apiWithResponse<T>(path, init)).body
}

const apiWithResponse = async <T,>(path: string, init: RequestInit = {}): Promise<{ body: T; status: number }> => {
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: init.body && !isForm ? { 'content-type': 'application/json', ...init.headers } : init.headers,
  })
  const body = await response.json() as T & { error?: string }
  if (!response.ok) throw new Error(body.error ?? 'request_failed')
  return { body, status: response.status }
}

const messageForError = (error: unknown) => {
  const code = error instanceof Error ? error.message : 'request_failed'
  if (code === 'invalid_credentials') return 'PINが違います。もう一度確認してください。'
  if (code === 'temporarily_locked') return '何度かPINが違いました。15分待ってから試してください。'
  if (code === 'already_setup') return '初期設定はすでに完了しています。画面を読み直してください。'
  if (code === 'invalid_setup') return '名前と4桁のPINを確認してください。'
  if (code === 'invalid_task') return '勉強内容を1〜100文字で入力してください。'
  if (code === 'duplicate_task') return '同じ勉強内容がすでに登録されています。'
  if (code === 'completed_task_confirmation_required') return '完了済みの課題です。確認してから変更してください。'
  if (code === 'approved_record_confirmation_required') return '承認済みの日です。確認してから変更してください。'
  if (code === 'tasks_incomplete') return '未完了の課題があるため、まだ承認できません。'
  if (code === 'approval_conflict') return '承認状態が更新されました。画面を読み直して確認してください。'
  if (code === 'treasure_locked') return 'おうちの人からOKをもらうと、宝箱を開けられます。'
  if (code === 'invalid_treasure_request') return '宝箱は今日の分だけ開けられます。'
  if (code === 'treasure_draw_conflict') return '宝箱の結果を確認できませんでした。画面を読み直してください。'
  if (code === 'treasure_not_opened') return '先に今日の宝箱を開けてね。'
  if (code === 'invalid_board_request') return '今日の宝箱の結果だけを使って進めます。'
  if (code === 'board_advance_conflict') return 'すごろくの位置を確認できませんでした。画面を読み直してください。'
  if (code === 'board_at_goal') return '今日はゴール！ 次のすごろくは明日以降の「すすむ！」で始まるよ。'
  if (code === 'invalid_reward') return 'ごほうびを1〜100文字で入力してください。'
  if (code === 'reward_not_found') return 'ごほうびが見つかりません。画面を読み直してください。'
  if (code === 'reward_ticket_required') return '先にごほうび券を発行してください。'
  if (code === 'goal_not_reached') return 'まだゴールしていません。'
  if (code === 'next_lap_conflict') return '次の周を開始できませんでした。画面を読み直してください。'
  if (code === 'rest_day') return '今日はお休みです。ゆっくり休んでね。'
  if (code === 'rest_day_activity_exists') return 'すでに学習や宝箱の記録があるため、お休みには変更できません。'
  if (code === 'invalid_study_date' || code === 'invalid_rest_day') return 'お休みの日付や理由を確認してください。'
  if (code === 'photo_result_unknown') return '送信結果を確認できませんでした。少し待って「画面を更新」で確認してください。写真は残してあります。'
  if (code === 'drive_not_configured') return '写真の保存先がまだ設定されていません。おうちの人に知らせてね。'
  if (code === 'photo_submission_disabled') return '写真提出は現在オフです。'
  if (code === 'invalid_photo_files') return '写真は1〜10枚、1枚2MB・合計20MB以内で選んでください。'
  if (code === 'invalid_photo_submission') return '写真を選び直して、もう一度送ってください。'
  if (code === 'drive_upload_failed' || code === 'drive_read_failed') return '写真を扱えませんでした。少し待ってもう一度お試しください。'
  if (code === 'photo_review_required') return '写真確認画面からOKしてください。'
  if (code === 'study_restart_treasure_opened') return '宝箱を開けた後は学習をやり直せません。'
  if (code === 'study_restart_not_allowed') return 'この学習は現在やり直せません。履歴を読み直して確認してください。'
  if (code === 'study_restart_conflict') return '学習状態が更新されました。履歴を読み直して確認してください。'
  if (code === 'invalid_restart_request') return 'やり直す学習の情報を確認できませんでした。'
  if (code === 'forbidden') return 'この操作を行う権限がありません。'
  return '通信に失敗しました。少し待ってからもう一度お試しください。'
}

function Brand({ step = 'STEP 16' }: { step?: string }) {
  return (
    <header className="brand">
      <span className="brandMark" aria-hidden="true">⌂</span>
      <span>おうちクエスト</span>
      <span className="stepBadge">{step}</span>
    </header>
  )
}

function PinField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        pattern="[0-9]{4}"
        maxLength={4}
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder="4桁"
        required
      />
    </label>
  )
}

function SetupScreen({ onComplete }: { onComplete: (user: User) => void }) {
  const [form, setForm] = useState({
    parentName: 'おとうさん', parentPin: '', childAName: '子どもA', childAPin: '', childBName: '子どもB', childBPin: '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const update = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = await api<{ user: User }>('/api/auth/setup', { method: 'POST', body: JSON.stringify(form) })
      onComplete(result.user)
    } catch (caught) {
      setError(messageForError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="shell authShell">
      <Brand />
      <section className="authCard">
        <p className="eyebrow">さいしょの設定</p>
        <h1>家族の入口をつくろう</h1>
        <p className="lead">それぞれ違う4桁のPINを決めてください。PINは安全に変換して保存されます。</p>
        <form onSubmit={submit}>
          <div className="familySetup">
            <fieldset>
              <legend>保護者</legend>
              <label className="field"><span>表示名</span><input value={form.parentName} maxLength={40} onChange={(e) => update('parentName', e.target.value)} required /></label>
              <PinField label="保護者PIN" value={form.parentPin} onChange={(value) => update('parentPin', value)} />
            </fieldset>
            <fieldset>
              <legend>小学6年生</legend>
              <label className="field"><span>表示名</span><input value={form.childAName} maxLength={40} onChange={(e) => update('childAName', e.target.value)} required /></label>
              <PinField label="子どもPIN" value={form.childAPin} onChange={(value) => update('childAPin', value)} />
            </fieldset>
            <fieldset>
              <legend>小学3年生</legend>
              <label className="field"><span>表示名</span><input value={form.childBName} maxLength={40} onChange={(e) => update('childBName', e.target.value)} required /></label>
              <PinField label="子どもPIN" value={form.childBPin} onChange={(value) => update('childBPin', value)} />
            </fieldset>
          </div>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primaryButton" disabled={busy || Object.values(form).some((value) => !value)}>{busy ? '設定中…' : 'この内容ではじめる'}</button>
        </form>
      </section>
    </main>
  )
}

function LoginScreen({ users, onLogin }: { users: User[]; onLogin: (user: User) => void }) {
  const [selectedId, setSelectedId] = useState(users[0]?.id ?? '')
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = await api<{ user: User }>('/api/auth/login', {
        method: 'POST', body: JSON.stringify({ userId: selectedId, pin }),
      })
      onLogin(result.user)
    } catch (caught) {
      setPin('')
      setError(messageForError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="shell authShell">
      <Brand />
      <section className="authCard loginCard">
        <p className="eyebrow">だれが使いますか？</p>
        <h1>じぶんをえらんでね</h1>
        <form onSubmit={submit}>
          <div className="userGrid">
            {users.map((user) => (
              <button
                className={`userButton ${selectedId === user.id ? 'selected' : ''}`}
                type="button"
                key={user.id}
                onClick={() => { setSelectedId(user.id); setError('') }}
                aria-pressed={selectedId === user.id}
              >
                <span className="userIcon" aria-hidden="true">{user.role === 'parent' ? '♢' : '★'}</span>
                <strong>{user.name}</strong>
                <small>{user.role === 'parent' ? '保護者' : '子ども'}</small>
              </button>
            ))}
          </div>
          <PinField label="4桁のPIN" value={pin} onChange={setPin} />
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primaryButton" disabled={busy || !selectedId || pin.length !== 4}>{busy ? '確認中…' : 'はいろう'}</button>
        </form>
      </section>
    </main>
  )
}

const displayDate = (date: string) => {
  const value = new Date(`${date}T00:00:00+09:00`)
  return new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', month: 'long', day: 'numeric', weekday: 'short' }).format(value)
}

const BOARD_AREAS = [
  { name: '草原', icon: '🌱', className: 'meadow', start: 1, end: 20 },
  { name: '森', icon: '🌳', className: 'forest', start: 21, end: 40 },
  { name: '海', icon: '🌊', className: 'sea', start: 41, end: 60 },
  { name: '宇宙', icon: '🚀', className: 'space', start: 61, end: 80 },
  { name: 'お城', icon: '🏰', className: 'castle', start: 81, end: 99 },
]
const boardArea = (position: number) => BOARD_AREAS.find((area) => position >= area.start && position <= area.end)
const rollEffectLabel = (effect: AppliedRollEffect) => ({
  none: 'なし', half: '半分', minimum_3: '最低3', minimum_4: '最低4', double: '2倍', half_blocked: 'バリアで半分を無効',
})[effect]

const boardCells = (goal: number) => Array.from({ length: Math.ceil(goal / 5) }, (_, row) => {
  const cells = Array.from({ length: 5 }, (__, column) => row * 5 + column + 1)
    .filter((position) => position <= goal)
  return row % 2 === 0 ? cells : cells.reverse()
}).flat()

function SugorokuBoard({ board, displayPosition, moving, celebration, extraRollReady, onExtraRoll }: {
  board: BoardState
  displayPosition: number
  moving: boolean
  celebration: BoardCelebration | null
  extraRollReady: boolean
  onExtraRoll: () => void
}) {
  const events = new Map((board.events ?? []).map((event) => [event.position, event]))
  const movement = board.todayMovement
  const currentArea = board.boardVersion === 3 ? boardArea(displayPosition) : undefined
  const rollEffectMessage = movement?.appliedRollEffect === 'half'
    ? `出た目${movement.requestedMoveCount} → 半分で${movement.effectiveMoveCount}マス`
    : movement?.appliedRollEffect === 'minimum_3'
      ? `出た目${movement.requestedMoveCount} → 最低3で${movement.effectiveMoveCount}マス`
      : movement?.appliedRollEffect === 'minimum_4' || movement?.appliedRollEffect === 'double'
      ? `出た目${movement.requestedMoveCount} → ${rollEffectLabel(movement.appliedRollEffect)}で${movement.effectiveMoveCount}マス`
    : movement?.appliedRollEffect === 'half_blocked'
        ? `バリアで半分をガード！ 出た目${movement.requestedMoveCount}のまま`
        : null
  return (
    <section className={`sugorokuCard ${board.reachedGoal ? 'goalReached' : ''}`} aria-labelledby="board-title">
      <div className="boardHeading">
        <div><p className="eyebrow">{board.currentLap}周目のすごろく</p><h2 id="board-title">ゴールまで進もう！</h2></div>
        <strong><span>{displayPosition}</span> / {board.goal}</strong>
      </div>
      {board.boardVersion === 3 && <p className={`currentBoardArea area-${currentArea?.className ?? 'meadow'}`} aria-live="polite">
        {displayPosition === 100 ? '🏆 GOAL！' : currentArea ? `${currentArea.icon} ${currentArea.name}を冒険中` : '🌱 草原から冒険をはじめよう！'}
      </p>}
      <div className={`startSquare ${displayPosition === 0 ? 'current' : ''}`}>
        <span>START</span>{displayPosition === 0 && <b aria-label="いまここ">★</b>}
      </div>
      {(board.nextRollEffect !== 'none' || board.hasBarrier) && <div className="boardEffects" aria-label="もっている効果">
        {board.nextRollEffect === 'half' && <span className="half">次は半分</span>}
        {board.nextRollEffect === 'minimum_3' && <span className="minimum">次は最低3</span>}
        {board.nextRollEffect === 'minimum_4' && <span className="minimum">次は最低4</span>}
        {board.nextRollEffect === 'double' && <span className="minimum">次は2倍</span>}
        {board.hasBarrier && <span className="barrier">盾 バリア</span>}
      </div>}
      <div className={`boardGrid board-${board.goal}`} aria-label={`${board.goal}マスのすごろく。現在${displayPosition}マス`}>
        {boardCells(board.goal).map((position) => {
          const squareEvent = events.get(position)
          const area = board.boardVersion === 3 ? boardArea(position) : undefined
          const boundary = area && position === area.start
          return <Fragment key={position}>
          {boundary && <div className={`boardAreaHeading area-${area.className}`}><strong>{area.icon} {area.name}</strong><span>{area.start}〜{area.end}マス</span></div>}
          <div data-direction={position % 5 === 0 ? 'down' : Math.floor((position - 1) / 5) % 2 === 0 ? 'right' : 'left'} aria-current={displayPosition === position ? 'step' : undefined} aria-label={`${position}マス${squareEvent ? `：${squareEvent.label}` : position === board.goal ? '：GOAL' : ''}`} className={`boardSquare ${area ? `area-${area.className}` : ''} ${(board.boardVersion ?? 1) === 1 && position % 5 === 0 ? 'special' : ''} ${squareEvent ? `event-${squareEvent.type}` : ''} ${displayPosition === position ? 'current' : ''} ${displayPosition === position && moving ? 'stepping' : ''} ${position === board.goal ? 'goalSquare' : ''}`} key={position}>
            <span>{position}</span>
            {position === board.goal && <small>{board.boardVersion === 3 ? '🏆 GOAL' : 'GOAL'}</small>}
            {squareEvent && <small className="eventLabel"><i aria-hidden="true">{squareEvent.icon}</i>{squareEvent.label}</small>}
            {displayPosition === position && <b aria-label="いまここ">★</b>}
          </div></Fragment>
        })}
      </div>
      {celebration && <div className={`boardCelebration ${celebration.kind}`} role="status" aria-live="assertive">
        <span aria-hidden="true">{celebration.icon}</span>
        <strong>{celebration.title}</strong>
        <small>{celebration.message}</small>
        {extraRollReady && <button type="button" className="extraRollButton" onClick={onExtraRoll} autoFocus>🎲 サイコロをふる！</button>}
      </div>}
      {moving && <p className="movingMessage" aria-live="polite">トコトコ… {displayPosition}マス！</p>}
      {!moving && movement && (rollEffectMessage || movement.eventMessage) && <div className="boardEventMessage" aria-live="polite">
        {rollEffectMessage && <small>{rollEffectMessage}</small>}
        {movement.eventMessage && <strong>{movement.eventMessage}</strong>}
      </div>}
      {!moving && board.reachedGoal && <div className="goalMessage" aria-live="polite"><strong>ゴールした！</strong><span>おめでとう！ 🎉</span></div>}
      {!moving && !board.reachedGoal && <p className="remainingSquares">あと <strong>{board.goal - board.currentPosition}</strong> マスでゴール！</p>}
    </section>
  )
}

const compressStudyPhoto = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) throw new Error('invalid_photo_files')
  let source: CanvasImageSource
  let width: number
  let height: number
  let release: () => void = () => undefined
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file)
    source = bitmap; width = bitmap.width; height = bitmap.height; release = () => bitmap.close()
  } else {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.src = url
    try { await image.decode() } catch { URL.revokeObjectURL(url); throw new Error('invalid_photo_files') }
    source = image; width = image.naturalWidth; height = image.naturalHeight; release = () => URL.revokeObjectURL(url)
  }
  const scale = Math.min(1, 1920 / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const context = canvas.getContext('2d')
  if (!context) { release(); throw new Error('invalid_photo_files') }
  try { context.drawImage(source, 0, 0, canvas.width, canvas.height) }
  finally { release() }
  let blob: Blob | null
  try { blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', .82)) }
  finally { canvas.width = 0; canvas.height = 0 }
  if (!blob || blob.size > MAX_PHOTO_BYTES) throw new Error('invalid_photo_files')
  return new File([blob], `${crypto.randomUUID()}.jpg`, { type: 'image/jpeg' })
}

function ChildHome({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [study, setStudy] = useState<DailyStudy | null>(null)
  const [weekPlan, setWeekPlan] = useState<ChildWeekPlan | null>(null)
  const [selectedPlanDate, setSelectedPlanDate] = useState('')
  const [weekPlanError, setWeekPlanError] = useState(false)
  const [error, setError] = useState('')
  const [busyTask, setBusyTask] = useState('')
  const [openingTreasure, setOpeningTreasure] = useState(false)
  const openingTreasureRef = useRef(false)
  const [movingBoard, setMovingBoard] = useState(false)
  const [displayPosition, setDisplayPosition] = useState(0)
  const [boardCelebration, setBoardCelebration] = useState<BoardCelebration | null>(null)
  const [extraRollReady, setExtraRollReady] = useState(false)
  const extraRollResolveRef = useRef<(() => void) | null>(null)
  const [photos, setPhotos] = useState<SelectedPhoto[]>([])
  const [photoBusy, setPhotoBusy] = useState(false)
  const photoBusyRef = useRef(false)
  const [photoProgress, setPhotoProgress] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [photoKey, setPhotoKey] = useState(() => crypto.randomUUID())
  const photosRef = useRef(photos)
  photosRef.current = photos

  const loadToday = async () => {
    const result = await api<DailyStudy>('/api/child/today')
    setStudy(result)
    setDisplayPosition(result.board.currentPosition)
  }

  const loadWeekPlan = async (selectToday = false) => {
    const result = await api<ChildWeekPlan>('/api/child/schedule')
    setWeekPlan(result)
    setWeekPlanError(false)
    setSelectedPlanDate((current) => selectToday || !result.days.some((day) => day.date === current)
      ? result.days.find((day) => day.isToday)?.date ?? result.days[0]?.date ?? ''
      : current)
  }

  useEffect(() => {
    void loadToday().catch((caught) => setError(messageForError(caught)))
    void loadWeekPlan(true).catch(() => setWeekPlanError(true))
    return () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url))
  }, [])

  const refreshHome = async () => {
    if (refreshing) return
    setRefreshing(true); setError('')
    try {
      await Promise.all([loadToday(), loadWeekPlan()])
    } catch (caught) {
      setError(messageForError(caught))
    } finally {
      setRefreshing(false)
    }
  }

  const completeTask = async (taskId: string) => {
    setBusyTask(taskId)
    setError('')
    try {
      setStudy(await api<DailyStudy>(`/api/child/today/tasks/${encodeURIComponent(taskId)}/complete`, { method: 'POST', body: '{}' }))
    } catch (caught) {
      setError(messageForError(caught))
    } finally {
      setBusyTask('')
    }
  }

  const addPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!selected.length || photoBusyRef.current) return
    if (photos.length + selected.length > MAX_PHOTO_COUNT) {
      setError(messageForError(new Error('invalid_photo_files')))
      return
    }
    photoBusyRef.current = true
    setPhotoBusy(true); setError('')
    try {
      const compressed: File[] = []
      for (const [index, file] of selected.entries()) {
        setPhotoProgress(`写真を準備中… ${index + 1}/${selected.length}枚`)
        await new Promise((resolve) => window.setTimeout(resolve, 0))
        compressed.push(await compressStudyPhoto(file))
      }
      if ([...photos.map((photo) => photo.file), ...compressed].reduce((sum, file) => sum + file.size, 0) > MAX_PHOTO_TOTAL_BYTES) {
        throw new Error('invalid_photo_files')
      }
      setPhotos((current) => [...current, ...compressed.map((file) => ({ id: crypto.randomUUID(), url: URL.createObjectURL(file), name: file.name, file }))])
    } catch (caught) { setError(messageForError(caught)) } finally { photoBusyRef.current = false; setPhotoBusy(false); setPhotoProgress('') }
  }

  const removePhoto = (id: string) => {
    if (photoBusyRef.current) return
    setPhotos((current) => {
      const target = current.find((photo) => photo.id === id)
      if (target) URL.revokeObjectURL(target.url)
      return current.filter((photo) => photo.id !== id)
    })
  }

  const clearPhotos = () => {
    setPhotos((current) => { current.forEach((photo) => URL.revokeObjectURL(photo.url)); return [] })
    setPhotoKey(crypto.randomUUID())
  }

  const submitPhotos = async () => {
    if (photoBusyRef.current || !study) return
    if (photos.length < 1 || photos.length > MAX_PHOTO_COUNT || photos.reduce((sum, photo) => sum + photo.file.size, 0) > MAX_PHOTO_TOTAL_BYTES) {
      setError(messageForError(new Error('invalid_photo_files')))
      return
    }
    const before = study
    photoBusyRef.current = true
    setPhotoBusy(true); setError(''); setPhotoProgress(`${photos.length}枚を送信・保存中…`)
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 120_000)
    const slowMessage = window.setTimeout(() => setPhotoProgress('保存に時間がかかっています。このままお待ちください…'), 15_000)
    const form = new FormData()
    form.set('idempotencyKey', photoKey)
    photos.forEach((photo) => form.append('images', photo.file, photo.file.name))
    try {
      const result = await api<DailyStudy>('/api/child/today/photos', { method: 'POST', body: form, signal: controller.signal })
      clearPhotos(); setStudy(result)
    } catch (caught) {
      window.clearTimeout(slowMessage)
      setPhotoProgress('送信結果を確認中…')
      const confirmed = await confirmPhotoSubmission(before, () =>
        api<DailyStudy>('/api/child/today', { signal: AbortSignal.timeout(10_000) }))
      if (confirmed) { clearPhotos(); setStudy(confirmed) }
      else {
        const code = caught instanceof Error ? caught.message : ''
        const knownFailure = ['invalid_photo_files', 'invalid_photo_submission', 'drive_not_configured',
          'photo_submission_disabled', 'tasks_incomplete', 'rest_day', 'unauthorized'].includes(code)
        setError(messageForError(knownFailure ? caught : new Error('photo_result_unknown')))
      }
    } finally {
      window.clearTimeout(timeout); window.clearTimeout(slowMessage)
      photoBusyRef.current = false; setPhotoBusy(false); setPhotoProgress('')
    }
  }

  const openTreasure = async () => {
    if (openingTreasureRef.current) return
    openingTreasureRef.current = true
    setOpeningTreasure(true)
    setError('')
    const audioContext = prepareRewardSound()
    const animationStarted = Date.now()
    try {
      const result = await apiWithResponse<DailyStudy>('/api/child/today/treasure/open', { method: 'POST', body: '{}' })
      if (shouldPlayTreasureSound(result.status)) playTreasureOpenSound(audioContext)
      const remainingAnimation = Math.max(0, 700 - (Date.now() - animationStarted))
      if (remainingAnimation) await new Promise((resolve) => window.setTimeout(resolve, remainingAnimation))
      setStudy(result.body)
    } catch (caught) {
      setError(messageForError(caught))
    } finally {
      openingTreasureRef.current = false
      setOpeningTreasure(false)
    }
  }

  const advanceBoard = async () => {
    if (movingBoard || !study?.board.canAdvance) return
    const audioContext = prepareRewardSound()
    setMovingBoard(true)
    setBoardCelebration(null)
    setError('')
    try {
      const result = await api<DailyStudy>('/api/child/today/board/advance', { method: 'POST', body: '{}' })
      const movement = result.board.todayMovement
      if (!movement) throw new Error('board_advance_conflict')
      if (movement.eventType === 'extra_roll' && movement.extraRollValue === null) throw new Error('board_advance_conflict')
      if (study.board.currentLap !== result.board.currentLap) {
        // Animate on the new board, including migration from a 30/50-square lap.
        setStudy({ ...result, board: { ...result.board, currentPosition: movement.fromPosition } })
      }
      setDisplayPosition(movement.fromPosition)
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const showExtraRoll = async () => {
        setBoardCelebration({ kind: 'event', icon: '🎲', title: 'もう1回！', message: 'サイコロをふってね！' })
        await new Promise<void>((resolve) => {
          extraRollResolveRef.current = resolve
          setExtraRollReady(true)
        })
        setExtraRollReady(false)
        setBoardCelebration({
          kind: 'event', icon: '🎲', title: `出た目 ${movement.extraRollValue}！`,
          message: `さらに${movement.extraRollValue}マスすすむ！`,
        })
        playBoardEventSound(audioContext, 'extra_roll')
        await new Promise((resolve) => window.setTimeout(resolve, 850))
        setBoardCelebration(null)
      }
      if (!reduceMotion) {
        for (let position = movement.fromPosition + 1; position <= movement.toPosition; position += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 260))
          setDisplayPosition(position)
          playBoardStepSound(audioContext, position)
          if (position === movement.baseToPosition && movement.eventType !== 'none') {
            if (movement.eventType === 'extra_roll') {
              await showExtraRoll()
              continue
            }
            const event = result.board.events.find((item) => item.position === movement.baseToPosition)
            setBoardCelebration({
              kind: 'event',
              icon: event?.icon ?? '★',
              title: event?.label ?? 'イベント！',
              message: movement.eventMessage ?? 'イベントマスにとまった！',
            })
            playBoardEventSound(audioContext, movement.eventType)
            await new Promise((resolve) => window.setTimeout(resolve, 850))
            setBoardCelebration(null)
          }
        }
      } else {
        if (movement.eventType === 'extra_roll') {
          setDisplayPosition(movement.baseToPosition)
          await showExtraRoll()
        }
        setDisplayPosition(movement.toPosition)
        if (movement.eventType !== 'none' && movement.eventType !== 'extra_roll') playBoardEventSound(audioContext, movement.eventType)
      }
      if (movement.reachedGoal) {
        setBoardCelebration({ kind: 'goal', icon: '🎉', title: 'GOAL!', message: 'ゴールした！ おめでとう！' })
        playBoardGoalSound(audioContext)
        if (!reduceMotion) await new Promise((resolve) => window.setTimeout(resolve, 1200))
      }
      setStudy(result)
    } catch (caught) {
      setError(messageForError(caught))
      try {
        const current = await api<DailyStudy>('/api/child/today')
        setStudy(current)
        setDisplayPosition(current.board.currentPosition)
      } catch { /* keep the original actionable error */ }
    } finally {
      extraRollResolveRef.current = null
      setExtraRollReady(false)
      setBoardCelebration(null)
      setMovingBoard(false)
    }
  }

  const allCompleted = Boolean(study && study.progress.total > 0 && study.progress.completed === study.progress.total)
  const selectedPlanDay = weekPlan?.days.find((day) => day.date === selectedPlanDate) ?? weekPlan?.days[0]

  return (
    <main className="shell childShell">
      <Brand />
      <div className="sessionBar childSession"><span><b>{user.name}</b> のクエスト</span><div className="sessionActions"><button disabled={refreshing} onClick={() => void refreshHome()}>{refreshing ? '更新中…' : '画面を更新'}</button><button onClick={onLogout}>きりかえる</button></div></div>
      <section className="questCard" aria-labelledby="quest-title">
        <div className="questHeading">
          <div><p className="eyebrow">きょうのクエスト</p><h1 id="quest-title">{user.name}、今日も一歩進もう！</h1></div>
          {study && <time dateTime={study.studyDate}>{displayDate(study.studyDate)}</time>}
        </div>

        {error && <p className="error" role="alert">{error}</p>}
        {!study && !error && <p className="loadingLine">今日の勉強を確認中…</p>}
        {study?.isRestDay ? (
          <div className="childRestDay" aria-live="polite">
            <span aria-hidden="true">☀️</span>
            <h2>きょうはおやすみ！</h2>
            <p>今日は休んでOK。ゆっくりすごしてね。</p>
            {study.restReason && <small>{study.restReason}</small>}
          </div>
        ) : study && (
          <>
            <div className="progressSummary" aria-live="polite">
              <div className="progressCopy"><strong>{study.progress.total}つ中 {study.progress.completed}つ完了</strong><span>{allCompleted ? 'ぜんぶできた！' : 'できたものから押してね'}</span></div>
              <div className="progressBar" aria-hidden="true"><span style={{ width: `${study.progress.total ? (study.progress.completed / study.progress.total) * 100 : 0}%` }} /></div>
            </div>
            <p className={`taskSource ${study.taskSource}`}>
              {study.taskSource === 'override' ? '今日だけのクエスト' : study.taskSource === 'schedule' ? 'いつもの予定から表示中' : '今日の予定はまだありません'}
            </p>
            {study.tasks.length === 0 ? (
              <div className="emptyPanel"><strong>今日の勉強はまだありません</strong><p>保護者に設定してもらってね。</p></div>
            ) : (
              <div className="childTaskList" aria-label="今日やる勉強">
                {study.tasks.map((task, index) => (
                  <article className={`childTask ${task.completedAt ? 'done' : ''}`} key={task.id}>
                    <span className="taskNumber">{task.completedAt ? '✓' : index + 1}</span>
                    <div><strong>{task.taskName}</strong><small>{task.completedAt ? '完了' : '未完了'}</small></div>
                    {!task.completedAt && <button disabled={busyTask === task.id} onClick={() => completeTask(task.id)}>{busyTask === task.id ? '…' : 'できた！'}</button>}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="weekPlanCard" aria-labelledby="week-plan-title">
        <div className="weekPlanHeading">
          <div><p className="eyebrow">これからの予定</p><h2 id="week-plan-title">1週間のクエスト</h2></div>
          <span>見るだけ</span>
        </div>
        {!weekPlan && !weekPlanError && <p className="loadingLine">予定を確認中…</p>}
        {weekPlanError && <p className="weekPlanError">予定を読み込めませんでした。今日のクエストはそのまま使えます。</p>}
        {weekPlan && selectedPlanDay && <>
          <div className="weekPlanTabs" role="tablist" aria-label="予定を見る日を選ぶ">
            {weekPlan.days.map((day) => {
              const selected = day.date === selectedPlanDay.date
              const weekday = WEEKDAYS.find((item) => item.value === day.dayOfWeek)?.short ?? ''
              return <button
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="selected-week-plan"
                className={`${selected ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                key={day.date}
                onClick={() => setSelectedPlanDate(day.date)}
              >
                <span>{Number(day.date.slice(8))}({weekday})</span>
                <small>{day.isToday ? '今日' : day.isRestDay ? '休み' : `${day.tasks.length}つ`}</small>
              </button>
            })}
          </div>
          <article
            id="selected-week-plan"
            role="tabpanel"
            className={`weekPlanDay ${selectedPlanDay.isToday ? 'today' : ''} ${selectedPlanDay.isRestDay ? 'rest' : ''}`}
          >
            <header>
              <time dateTime={selectedPlanDay.date}>{displayDate(selectedPlanDay.date)}</time>
              {selectedPlanDay.isToday && <strong>今日</strong>}
              {selectedPlanDay.isRestDay && <strong className="restBadge">おやすみ</strong>}
            </header>
            {selectedPlanDay.isRestDay ? (
              <p className="weekPlanRest">ゆっくり休む日{selectedPlanDay.restReason ? `・${selectedPlanDay.restReason}` : ''}</p>
            ) : selectedPlanDay.tasks.length > 0 ? (
              <>
                <p className={`weekPlanSource ${selectedPlanDay.taskSource}`}>
                  {selectedPlanDay.taskSource === 'override' ? 'この日だけの予定' : selectedPlanDay.taskSource === 'snapshot' ? 'その日に決まった予定' : 'いつもの予定'}
                </p>
                <ol>{selectedPlanDay.tasks.map((task, index) => <li key={`${selectedPlanDay.date}-${index}`}><span>{index + 1}</span><strong>{task.taskName}</strong></li>)}</ol>
              </>
            ) : <p className="weekPlanEmpty">予定はまだありません</p>}
          </article>
        </>}
        <p className="weekPlanNote">ここでは予定を見るだけです。変更はおとうさんが行います。</p>
      </section>

      {study && !study.isRestDay && study.photoSubmissionEnabled && allCompleted &&
       (study.photoSubmissionState === 'required' || study.photoSubmissionState === 'retry_requested') &&
       <section className="photoCard" aria-labelledby="photo-title">
        <div><p className="eyebrow">べんきょうの写真</p><h2 id="photo-title">がんばったところを見せよう</h2></div>
        {study.photoSubmissionState === 'retry_requested' && <p className="photoRetry">もう一度、見せたいところを撮って送ってね。</p>}
        <div className="photoActions">
          <label className="cameraButton">カメラで撮る<input disabled={photoBusy || photos.length >= MAX_PHOTO_COUNT} type="file" accept="image/*" capture="environment" onChange={addPhotos} /></label>
          <label className="libraryButton">写真から選ぶ<input disabled={photoBusy || photos.length >= MAX_PHOTO_COUNT} type="file" accept="image/*" multiple onChange={addPhotos} /></label>
        </div>
        {photos.length > 0 ? (
          <div className="photoGrid">
            {photos.map((photo, index) => (
              <figure key={photo.id}><img src={photo.url} alt={`選んだ勉強写真 ${index + 1}`} /><button disabled={photoBusy} onClick={() => removePhoto(photo.id)} aria-label={`${index + 1}枚目を取り消す`}>取り消す</button></figure>
            ))}
          </div>
        ) : <p className="photoHint">写真を1〜10枚選んでね。</p>}
        {photos.length > 0 && <div className="photoSubmitActions"><button onClick={clearPhotos} disabled={photoBusy}>全部取り消す</button><button className="photoSend" onClick={submitPhotos} disabled={photoBusy}>{photoBusy ? '処理中…' : `${photos.length}枚を送る`}</button></div>}
        {photoBusy && <p className="photoHint" role="status" aria-live="polite">{photoProgress}</p>}
        <p className="temporaryNote">写真は送信に適した大きさにしてから送ります。</p>
      </section>}

      {study && !study.isRestDay && (
        <section className={`childApproval ${study.status}`} aria-live="polite">
          {study.status === 'approved' ? (
            <><span aria-hidden="true">◎</span><div><strong>おうちの人からOKをもらったよ！</strong><p>今日のクエストは正式に完了！</p></div></>
          ) : study.photoSubmissionState === 'pending' ? (
            <><span aria-hidden="true">📷</span><div><strong>写真を送ったよ！</strong><p>おうちの人が写真を確認中です。</p></div></>
          ) : study.photoSubmissionState === 'required' || study.photoSubmissionState === 'retry_requested' ? (
            <><span aria-hidden="true">📸</span><div><strong>ぜんぶできた！</strong><p>写真を選んで送ってね。</p></div></>
          ) : study.status === 'pending' || allCompleted ? (
            <><span aria-hidden="true">⌛</span><div><strong>ぜんぶできた！</strong><p>おうちの人に確認してもらおう！</p></div></>
          ) : (
            <><span aria-hidden="true">☆</span><div><strong>クエストに挑戦中</strong><p>できた課題から「できた！」を押してね。</p></div></>
          )}
        </section>
      )}
      {study && !study.isRestDay && study.treasure.state === 'ready' && (
        <section className="treasureCard ready" aria-labelledby="treasure-title">
          <p className="eyebrow">きょうの宝箱</p>
          <h2 id="treasure-title">宝箱をあけよう！</h2>
          <button className={`treasureButton ${openingTreasure ? 'opening' : ''}`} disabled={openingTreasure} onClick={openTreasure}>
            <span className="treasureIcon" aria-hidden="true">🎁</span>
            <strong>{openingTreasure ? 'パカッ！' : 'タップしてあける'}</strong>
          </button>
        </section>
      )}
      {study && !study.isRestDay && study.treasure.state === 'opened' && study.treasure.moveCount && (
        <section className="treasureCard opened" aria-live="polite">
          <span className="openedSparkles" aria-hidden="true">✦ 🎁 ✦</span>
          <p>パカッ！</p>
          <strong>{study.treasure.moveCount}<small>マスすすむ！</small></strong>
          <span>{study.board.todayMovement ? 'すごろくに反映しました' : '今日の結果は保存されています'}</span>
        </section>
      )}
      {study && !study.isRestDay && <SugorokuBoard board={study.board} displayPosition={displayPosition} moving={movingBoard} celebration={boardCelebration} extraRollReady={extraRollReady} onExtraRoll={() => {
        const resolve = extraRollResolveRef.current
        extraRollResolveRef.current = null
        resolve?.()
      }} />}
      {study && !study.isRestDay && study.board.canAdvance && study.treasure.moveCount && (
        <button className="advanceButton" disabled={movingBoard} onClick={advanceBoard}>
          <strong>{study.board.startsNextLap ? '次のすごろくへ！ ' : ''}{study.treasure.moveCount}マスすすむ！</strong>
          <span>{movingBoard ? 'すすんでいるよ…' : 'タップして すすむ！'}</span>
        </button>
      )}
      {study && !study.isRestDay && study.board.reachedGoal && (
        <section className="rewardWonCard" aria-live="polite">
          <div className="confetti" aria-hidden="true">🎉 ✦ 🎊 ✦ 🎉</div>
          <p>ゴール！</p>
          <p>次のすごろくは、明日以降の「すすむ！」で自動ではじまるよ。</p>
          <h2>ごほうびゲット！</h2>
          {study.goalRewardTicket ? (
            <strong>{study.goalRewardTicket.rewardName}</strong>
          ) : (
            <span>おうちの人がごほうびを準備中だよ</span>
          )}
        </section>
      )}
      {study && study.rewardTickets.length > 0 && (
        <section className="childTicketWallet" aria-labelledby="ticket-wallet-title">
          <p className="eyebrow">もっているごほうび券</p>
          <h2 id="ticket-wallet-title">ごほうび券</h2>
          <div className="childTicketList">
            {study.rewardTickets.map((ticket) => (
              <article key={ticket.id}><span aria-hidden="true">🎟️</span><div><strong>{ticket.rewardName}</strong><small>{ticket.sourceGoalCount}周目のゴールでゲット</small></div></article>
            ))}
          </div>
          <p>使うときは、おうちの人に見せてね。</p>
        </section>
      )}
    </main>
  )
}

function ScheduleEditor() {
  const [schedule, setSchedule] = useState<SchedulePayload | null>(null)
  const [selectedChildId, setSelectedChildId] = useState('')
  const [selectedDay, setSelectedDay] = useState(1)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    setError('')
    try {
      const result = await api<SchedulePayload>('/api/parent/study/schedules')
      setSchedule(result)
      setSelectedChildId((current) => current || result.children[0]?.child.id || '')
    } catch (caught) { setError(messageForError(caught)) }
  }
  useEffect(() => { void load() }, [])

  const selectedChild = schedule?.children.find((item) => item.child.id === selectedChildId)
  const selectedTasks = selectedChild?.days.find((day) => day.dayOfWeek === selectedDay)?.tasks ?? []
  const selectedDayLabel = WEEKDAYS.find((day) => day.value === selectedDay)?.label ?? ''

  const addTask = async () => {
    const taskName = draft.trim()
    if (!taskName || !selectedChildId) return
    setBusy('add')
    try {
      setSchedule(await api<SchedulePayload>(`/api/parent/study/schedules/children/${encodeURIComponent(selectedChildId)}/days/${selectedDay}/tasks`, {
        method: 'POST', body: JSON.stringify({ taskName }),
      }))
      setDraft('')
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  const editTask = async (task: ScheduleTask) => {
    const taskName = window.prompt(`${selectedDayLabel}の通常予定を変更`, task.taskName)?.trim()
    if (!taskName || taskName === task.taskName) return
    setBusy(task.id)
    try {
      setSchedule(await api<SchedulePayload>(`/api/parent/study/schedules/children/${encodeURIComponent(selectedChildId)}/tasks/${encodeURIComponent(task.id)}`, {
        method: 'PATCH', body: JSON.stringify({ taskName }),
      }))
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  const deleteTask = async (task: ScheduleTask) => {
    if (!window.confirm(`「${task.taskName}」を${selectedDayLabel}の通常予定から削除しますか？\n過去の完了記録は消えません。`)) return
    setBusy(task.id)
    try {
      setSchedule(await api<SchedulePayload>(`/api/parent/study/schedules/children/${encodeURIComponent(selectedChildId)}/tasks/${encodeURIComponent(task.id)}`, {
        method: 'DELETE', body: '{}',
      }))
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  return (
    <div className="scheduleEditor">
      <div className="scheduleIntro"><p className="eyebrow">通常予定</p><h1>曜日ごとのクエスト</h1><p>一度決めれば、毎週同じ曜日に自動で表示されます。</p></div>
      {error && <p className="error" role="alert">{error}</p>}
      {!schedule && !error && <p className="loadingLine">通常予定を確認中…</p>}
      {schedule && (
        <>
          <div className="childTabs" aria-label="子どもを選ぶ">
            {schedule.children.map((child) => <button key={child.child.id} className={selectedChildId === child.child.id ? 'selected' : ''} aria-pressed={selectedChildId === child.child.id} onClick={() => setSelectedChildId(child.child.id)}>{child.child.name}</button>)}
          </div>
          <div className="weekdayTabs" aria-label="曜日を選ぶ">
            {WEEKDAYS.map((day) => <button key={day.value} className={selectedDay === day.value ? 'selected' : ''} aria-pressed={selectedDay === day.value} onClick={() => setSelectedDay(day.value)}><span>{day.short}</span><small>{selectedChild?.days.find((item) => item.dayOfWeek === day.value)?.tasks.length ?? 0}</small></button>)}
          </div>
          <section className="scheduleDay" aria-labelledby="schedule-day-title">
            <div className="scheduleDayHeader"><div><span>毎週</span><h2 id="schedule-day-title">{selectedDayLabel}</h2></div><strong>{selectedTasks.length}つ</strong></div>
            <div className="scheduleTaskList">
              {selectedTasks.length === 0 && <div className="noTasks"><strong>通常予定はまだありません</strong><p>下から課題を追加できます。</p></div>}
              {selectedTasks.map((task, index) => (
                <div className="scheduleTask" key={task.id}><span className="taskNumber">{index + 1}</span><strong>{task.taskName}</strong><div><button disabled={busy === task.id} onClick={() => editTask(task)}>編集</button><button className="deleteButton" disabled={busy === task.id} onClick={() => deleteTask(task)}>削除</button></div></div>
              ))}
            </div>
            <form className="addTaskForm scheduleAdd" onSubmit={(event) => { event.preventDefault(); void addTask() }}>
              <label><span>{selectedDayLabel}に追加</span><input value={draft} maxLength={100} placeholder="例：算数プリント 1枚" onChange={(event) => setDraft(event.target.value)} /></label>
              <button disabled={busy === 'add' || !draft.trim()}>追加する</button>
            </form>
          </section>
          <p className="scheduleNote">通常予定を変えても、過去の課題や完了記録は変わりません。</p>
        </>
      )}
    </div>
  )
}

const displayDateTime = (value: string) => new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(value))

function RewardEditor() {
  const [payload, setPayload] = useState<RewardPayload | null>(null)
  const [selectedChildId, setSelectedChildId] = useState('')
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    api<RewardPayload>('/api/parent/study/rewards').then((result) => {
      setPayload(result)
      setSelectedChildId(result.children[0]?.child.id ?? '')
    }).catch((caught) => setError(messageForError(caught)))
  }, [])

  const child = payload?.children.find((item) => item.child.id === selectedChildId)

  const mutate = async (key: string, path: string, init: RequestInit) => {
    setBusy(key)
    setError('')
    try { setPayload(await api<RewardPayload>(path, init)) }
    catch (caught) { setError(messageForError(caught)) }
    finally { setBusy('') }
  }

  const addReward = async (event: FormEvent) => {
    event.preventDefault()
    const name = draft.trim()
    if (!name || !child) return
    await mutate('add', `/api/parent/study/rewards/children/${encodeURIComponent(child.child.id)}`, {
      method: 'POST', body: JSON.stringify({ name }),
    })
    setDraft('')
  }

  const editReward = async (reward: RewardCandidate) => {
    if (!child) return
    const name = window.prompt('ごほうびの内容を変更', reward.name)?.trim()
    if (!name || name === reward.name) return
    await mutate(reward.id, `/api/parent/study/rewards/children/${encodeURIComponent(child.child.id)}/${encodeURIComponent(reward.id)}`, {
      method: 'PATCH', body: JSON.stringify({ name }),
    })
  }

  const deleteReward = async (reward: RewardCandidate) => {
    if (!child || !window.confirm(`「${reward.name}」を候補から削除しますか？\n発行済みの券は残ります。`)) return
    await mutate(reward.id, `/api/parent/study/rewards/children/${encodeURIComponent(child.child.id)}/${encodeURIComponent(reward.id)}`, {
      method: 'DELETE', body: '{}',
    })
  }

  const selectReward = async (reward: RewardCandidate) => {
    if (!child || reward.selectedForNextGoal) return
    await mutate(`select-${reward.id}`, `/api/parent/study/rewards/children/${encodeURIComponent(child.child.id)}/${encodeURIComponent(reward.id)}/select`, {
      method: 'POST', body: '{}',
    })
  }

  const changeTicketStatus = async (ticket: RewardTicket, status: 'available' | 'used') => {
    const action = status === 'used' ? '使用済みにします' : '未使用に戻します'
    if (!window.confirm(`「${ticket.rewardName}」を${action}か？`)) return
    await mutate(ticket.id, `/api/parent/study/reward-tickets/${encodeURIComponent(ticket.id)}`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    })
  }


  return (
    <div className="rewardEditor">
      <div className="scheduleIntro"><p className="eyebrow">ごほうび管理</p><h1>次のゴールを楽しみに</h1><p>子どもごとに、次のゴールでもらえるごほうびを選べます。</p></div>
      {error && <p className="error" role="alert">{error}</p>}
      {!payload && !error && <p className="loadingLine">ごほうびを確認中…</p>}
      {payload && <div className="childTabs" aria-label="子どもを選ぶ">
        {payload.children.map((item) => <button key={item.child.id} className={selectedChildId === item.child.id ? 'selected' : ''} aria-pressed={selectedChildId === item.child.id} onClick={() => setSelectedChildId(item.child.id)}>{item.child.name}</button>)}
      </div>}
      {child && <>
        <section className="rewardStatusCard">
          <div><span>{child.board.currentLap}周目</span><strong>{child.board.currentPosition} / {child.board.goal}マス</strong></div>
          {child.board.currentPosition === child.board.goal && <p>次のすごろくは、翌日以降に子どもが「すすむ！」を押すと自動で始まります。写真の確認・承認はそのまま行えます。</p>}
        </section>
        <section className="rewardCandidates" aria-labelledby="reward-candidates-title">
          <div className="rewardSectionHeading"><div><p className="eyebrow">ごほうび候補</p><h2 id="reward-candidates-title">次にもらえるもの</h2></div><strong>{child.rewards.length}件</strong></div>
          <div className="rewardCandidateList">
            {child.rewards.length === 0 && <p className="noTasks">候補を登録すると、最初の1件が次のごほうびになります。</p>}
            {child.rewards.map((reward) => (
              <article className={reward.selectedForNextGoal ? 'selected' : ''} key={reward.id}>
                <button className="rewardSelect" disabled={busy === `select-${reward.id}`} onClick={() => selectReward(reward)} aria-pressed={reward.selectedForNextGoal}>
                  <span>{reward.selectedForNextGoal ? '✓' : '○'}</span><strong>{reward.name}</strong><small>{reward.selectedForNextGoal ? '次のごほうび' : 'これを選ぶ'}</small>
                </button>
                <div><button disabled={busy === reward.id} onClick={() => editReward(reward)}>編集</button><button className="deleteButton" disabled={busy === reward.id} onClick={() => deleteReward(reward)}>削除</button></div>
              </article>
            ))}
          </div>
          <form className="addTaskForm" onSubmit={addReward}><label><span>候補を追加</span><input maxLength={100} value={draft} placeholder="例：アイスを1つ" onChange={(event) => setDraft(event.target.value)} /></label><button disabled={busy === 'add' || !draft.trim()}>追加する</button></form>
        </section>
        <section className="ticketManager" aria-labelledby="ticket-manager-title">
          <div className="rewardSectionHeading"><div><p className="eyebrow">発行済み</p><h2 id="ticket-manager-title">ごほうび券</h2></div><strong>{child.tickets.length}枚</strong></div>
          {child.tickets.length === 0 ? <p className="noTasks">まだごほうび券はありません。</p> : <div className="ticketManagerList">
            {child.tickets.map((ticket) => (
              <article className={ticket.status === 'used' ? 'used' : ''} key={ticket.id}>
                <span aria-hidden="true">🎟️</span><div><strong>{ticket.rewardName}</strong><small>{ticket.sourceGoalCount}周目・{displayDateTime(ticket.acquiredAt)}発行</small>{ticket.usedAt && <small>{displayDateTime(ticket.usedAt)}使用</small>}</div>
                <button disabled={busy === ticket.id} onClick={() => changeTicketStatus(ticket, ticket.status === 'used' ? 'available' : 'used')}>{ticket.status === 'used' ? '未使用に戻す' : '使用済みにする'}</button>
              </article>
            ))}
          </div>}
        </section>
      </>}
    </div>
  )
}

const DASHBOARD_LABELS: Record<DashboardChild['todayStatus'], string> = {
  rest: 'お休み', goal: 'ゴール', board_moved: 'すごろく進行済み', treasure_opened: '宝箱開封済み',
  approved: '承認済み', photo_pending: '写真確認待ち', photo_required: '写真提出待ち',
  pending: '承認待ち', studying: '学習中', not_started: '学習前',
}

function PhotoManager() {
  const [payload, setPayload] = useState<PhotoAdminPayload | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<{ url: string; alt: string } | null>(null)
  const load = async () => {
    try { setPayload(await api<PhotoAdminPayload>('/api/parent/study/photos')) }
    catch (caught) { setError(messageForError(caught)) }
  }
  useEffect(() => { void load() }, [])
  useEffect(() => {
    if (!preview) return
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setPreview(null) }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [preview])
  const review = async (submissionId: string, decision: 'approve' | 'retry') => {
    const text = decision === 'approve' ? '写真をOKにして今日の学習を承認しますか？' : '写真を削除して、もう一度お願いする状態にしますか？'
    if (!window.confirm(text)) return
    setBusy(submissionId); setError('')
    try {
      setPayload(await api<PhotoAdminPayload>(`/api/parent/study/photos/submissions/${encodeURIComponent(submissionId)}/review`, {
        method: 'POST', body: JSON.stringify({ decision }),
      }))
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }
  return <div className="photoManager">
    <div className="scheduleIntro"><p className="eyebrow">学習写真</p><h1>写真を確認する</h1><p>届いた写真を確認して、「もう一度お願い」または「OK！」を選びます。</p></div>
    {error && <p className="error" role="alert">{error}</p>}
    {!payload && !error && <p className="loadingLine">写真の状態を確認中…</p>}
    {payload && payload.pending.length === 0 && <p className="noTasks">写真確認待ちはありません。</p>}
    {payload && payload.cleanup.deleteFailedCount > 0 && <p className="error" role="status">Driveから削除できていない写真が{payload.cleanup.deleteFailedCount}枚あります。画面を開くたびに少しずつ再試行します。</p>}
    <div className="photoReviewList">{payload?.pending.map((submission) => <article key={submission.id}>
      <header><div><h2>{submission.childName}</h2><span>{displayDate(submission.studyDate)}・{submission.imageCount}枚</span></div><small>{displayDateTime(submission.submittedAt)} 提出</small></header>
      <h3 className="photoQuestHeading">この写真のクエスト</h3>
      <ul>{submission.tasks.map((task, index) => <li key={`${task.taskName}-${index}`}>✓ {task.taskName}</li>)}</ul>
      <div className="parentPhotoGrid">{submission.images.map((image, index) => {
        const url = `${API_BASE}/api/parent/study/photos/submissions/${encodeURIComponent(submission.id)}/images/${encodeURIComponent(image.id)}`
        const alt = `${submission.childName}の提出写真 ${index + 1}`
        return <button key={image.id} type="button" aria-label={`${alt}を大きく見る`} onClick={() => setPreview({ url, alt })}><img src={url} alt={alt} /></button>
      })}</div>
      <div className="photoReviewActions"><button disabled={busy === submission.id} onClick={() => void review(submission.id, 'retry')}>もう一度お願い</button><button className="approveButton" disabled={busy === submission.id} onClick={() => void review(submission.id, 'approve')}>OK！</button></div>
    </article>)}</div>
    {preview && <div className="photoLightbox" role="dialog" aria-modal="true" aria-label="学習写真の拡大表示" onClick={() => setPreview(null)}>
      <div className="photoLightboxContent" onClick={(event) => event.stopPropagation()}>
        <button className="photoLightboxClose" type="button" autoFocus aria-label="拡大写真を閉じる" onClick={() => setPreview(null)}><span aria-hidden="true">×</span> 閉じる</button>
        <img src={preview.url} alt={preview.alt} />
      </div>
    </div>}
  </div>
}

function ParentSettings() {
  const [payload, setPayload] = useState<PhotoAdminPayload | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  useEffect(() => {
    api<PhotoAdminPayload>('/api/parent/study/photos').then(setPayload).catch((caught) => setError(messageForError(caught)))
  }, [])
  const setPhotoEnabled = async (childId: string, enabled: boolean) => {
    setBusy(childId); setError('')
    try {
      setPayload(await api<PhotoAdminPayload>(`/api/parent/study/photos/children/${encodeURIComponent(childId)}/settings`, {
        method: 'PATCH', body: JSON.stringify({ enabled }),
      }))
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }
  return <div className="parentSettings">
    <div className="scheduleIntro"><p className="eyebrow">保護者向け設定</p><h1>設定</h1><p>おうちクエストの使い方を子どもごとに設定します。</p></div>
    {error && <p className="error" role="alert">{error}</p>}
    {!payload && !error && <p className="loadingLine">設定を確認中…</p>}
    {payload && <section className="settingsSection" aria-labelledby="photo-settings-title">
      <div><p className="eyebrow">学習写真</p><h2 id="photo-settings-title">写真提出の設定</h2></div>
      <p>ONにすると、クエスト完了後に学習写真の提出が必要になります。</p>
      <div className="photoSettings">{payload.settings.map((setting) => <label key={setting.childId}>
        <span><strong>{setting.childName}</strong><small>学習写真を提出する</small></span>
        <input type="checkbox" checked={setting.photoSubmissionEnabled} disabled={busy === setting.childId} onChange={(event) => void setPhotoEnabled(setting.childId, event.target.checked)} />
      </label>)}</div>
    </section>}
  </div>
}

function ParentDashboard({ onNavigate }: { onNavigate: (view: ParentView) => void }) {
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null)
  const [restDates, setRestDates] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const load = async () => {
    try { setDashboard(await api<DashboardPayload>('/api/parent/dashboard')) }
    catch (caught) { setError(messageForError(caught)) }
  }
  useEffect(() => { void load() }, [])

  const toggleRest = async (child: DashboardChild) => {
    const date = restDates[child.child.id] || dashboard?.studyDate
    if (!date) return
    const existing = child.upcomingRestDays.some((item) => item.restDate === date)
    const wording = existing ? 'お休みを解除しますか？' : `${displayDate(date)}をお休みにしますか？`
    if (!window.confirm(wording)) return
    setBusy(child.child.id)
    setError('')
    try {
      setDashboard(await api<DashboardPayload>(`/api/parent/study/children/${encodeURIComponent(child.child.id)}/rest-days/${date}`, {
        method: existing ? 'DELETE' : 'PUT', body: '{}',
      }))
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  return <div className="dashboardView">
    <div className="parentHeading"><div><p className="eyebrow">保護者ダッシュボード</p><h1>今日の家族クエスト</h1></div>{dashboard && <time>{displayDate(dashboard.studyDate)}</time>}</div>
    <p className="lead">今日どこまで進んでいるかを、ひと目で確認できます。</p>
    {error && <p className="error" role="alert">{error}</p>}
    {!dashboard && !error && <p className="loadingLine">今日の状態を確認中…</p>}
    <div className="dashboardChildren">
      {dashboard?.children.map((child) => {
        const selectedDate = restDates[child.child.id] || dashboard.studyDate
        const selectedIsRest = child.upcomingRestDays.some((item) => item.restDate === selectedDate)
        return <article className={`dashboardChild ${child.todayStatus}`} key={child.child.id}>
          <header><div><span className="childAvatar">★</span><h2>{child.child.name}</h2></div><strong>{DASHBOARD_LABELS[child.todayStatus]}</strong></header>
          {child.isRestDay ? <div className="dashboardRest">☀️ 今日は休んでOK</div> : <>
            <div className="dashboardProgress"><span>今日の課題</span><strong>{child.progress.completed} / {child.progress.total}</strong></div>
            <div className="miniProgress"><span style={{ width: `${child.progress.total ? child.progress.completed / child.progress.total * 100 : 0}%` }} /></div>
          </>}
          <dl className="dashboardStats">
            {child.photoSubmissionEnabled && <div><dt>学習写真</dt><dd>{child.photoSubmissionState === 'pending' ? '確認待ち' : child.photoSubmissionState === 'required' || child.photoSubmissionState === 'retry_requested' ? '提出待ち' : child.photoSubmissionState === 'approved' ? 'OK' : '—'}</dd></div>}
            <div><dt>宝箱</dt><dd>{child.treasure.state === 'opened' ? `${child.treasure.moveCount}マス` : child.treasure.state === 'ready' ? '開けられる' : 'まだ'}</dd></div>
            <div><dt>今日の移動</dt><dd>{child.todayMoveCount ? `+${child.todayMoveCount}` : '—'}</dd></div>
            <div><dt>すごろく</dt><dd>{child.board.currentPosition} / {child.board.goal}</dd></div>
            <div><dt>周回</dt><dd>{child.board.currentLap}周目</dd></div>
            <div><dt>未使用券</dt><dd>{child.availableRewardTicketCount}枚</dd></div>
          </dl>
          <div className="restControl">
            <label><span>お休みの日</span><input type="date" min={dashboard.studyDate} value={selectedDate} onChange={(event) => setRestDates((current) => ({ ...current, [child.child.id]: event.target.value }))} /></label>
            <button className={selectedIsRest ? 'clearRest' : ''} disabled={busy === child.child.id} onClick={() => toggleRest(child)}>{selectedIsRest ? 'お休みを解除' : 'お休みにする'}</button>
          </div>
          {child.photoSubmissionState === 'pending' && <button className="dashboardPhotoLink" onClick={() => onNavigate('photos')}>届いた写真を確認する</button>}
        </article>
      })}
    </div>
    <nav className="dashboardLinks" aria-label="管理画面へのショートカット">
      <button onClick={() => onNavigate('today')}>今日の課題管理</button><button onClick={() => onNavigate('schedule')}>曜日別通常予定</button>
      <button onClick={() => onNavigate('photos')}>学習写真を確認</button><button onClick={() => onNavigate('rewards')}>ごほうび・券</button><button onClick={() => onNavigate('history')}>過去30日の履歴</button>
    </nav>
  </div>
}

function HistoryView() {
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null)
  const [selectedChildId, setSelectedChildId] = useState('')
  const [history, setHistory] = useState<HistoryPayload | null>(null)
  const [error, setError] = useState('')
  const [restartTarget, setRestartTarget] = useState<HistoryDay | null>(null)
  const [restartBusy, setRestartBusy] = useState(false)
  useEffect(() => {
    api<DashboardPayload>('/api/parent/dashboard').then((result) => {
      setDashboard(result); setSelectedChildId(result.children[0]?.child.id ?? '')
    }).catch((caught) => setError(messageForError(caught)))
  }, [])
  useEffect(() => {
    if (!selectedChildId) return
    setHistory(null); setError('')
    api<HistoryPayload>(`/api/parent/history/children/${encodeURIComponent(selectedChildId)}?days=30`)
      .then(setHistory).catch((caught) => setError(messageForError(caught)))
  }, [selectedChildId])

  const restartStudy = async () => {
    if (!history || !restartTarget || restartBusy) return
    setRestartBusy(true); setError('')
    try {
      await api(`/api/parent/history/children/${encodeURIComponent(history.child.id)}/days/${encodeURIComponent(restartTarget.studyDate)}/restart`, {
        method: 'POST',
        body: JSON.stringify({
          expectedTaskRevision: restartTarget.taskRevision,
          expectedApprovalSequence: restartTarget.approvalSequence,
        }),
      })
      setRestartTarget(null)
      setHistory(await api<HistoryPayload>(`/api/parent/history/children/${encodeURIComponent(history.child.id)}?days=30`))
    } catch (caught) {
      setError(messageForError(caught))
      setRestartTarget(null)
      setHistory(await api<HistoryPayload>(`/api/parent/history/children/${encodeURIComponent(history.child.id)}?days=30`).catch(() => history))
    } finally {
      setRestartBusy(false)
    }
  }
  return <div className="historyView">
    <div className="scheduleIntro"><p className="eyebrow">過去の記録</p><h1>30日間のクエスト履歴</h1><p>その日に保存された課題名・承認・宝箱・移動・ごほうびを表示します。</p></div>
    {dashboard && <div className="childTabs">{dashboard.children.map((item) => <button key={item.child.id} className={selectedChildId === item.child.id ? 'selected' : ''} onClick={() => setSelectedChildId(item.child.id)}>{item.child.name}</button>)}</div>}
    {error && <p className="error" role="alert">{error}</p>}
    {!history && !error && <p className="loadingLine">履歴を確認中…</p>}
    {history && history.days.length === 0 && <p className="noTasks">この30日間の記録はまだありません。</p>}
    <div className="historyList">{history?.days.map((day) => <details key={day.studyDate} className={day.isRestDay ? 'rest' : ''}>
      <summary><div><strong>{displayDate(day.studyDate)}</strong><span>{day.isRestDay ? 'お休み' : `${day.progress.completed}/${day.progress.total} 完了`}</span></div><b>{day.movement?.reachedGoal ? 'GOAL!' : day.approved ? '承認済み' : day.status === 'pending' ? '承認待ち' : ''}</b></summary>
      <div className="historyDetail">
        {day.isRestDay ? <p className="historyRest">☀️ 今日はお休み{day.restReason ? `：${day.restReason}` : ''}</p> : <>
          <ul>{day.tasks.map((task, index) => <li key={`${task.taskName}-${index}`}><span>{task.completedAt ? '✓' : '○'}</span><strong>{task.taskName}</strong><small>{task.completedAt ? '完了' : '未完了'}</small></li>)}</ul>
          <dl><div><dt>保護者承認</dt><dd>{day.approved ? '済' : '未'}</dd></div><div><dt>宝箱の出た目</dt><dd>{day.treasure ? `${day.treasure.moveCount}` : '—'}</dd></div>
            {day.movement?.appliedRollEffect && day.movement.appliedRollEffect !== 'none' && <div><dt>次回効果</dt><dd>{rollEffectLabel(day.movement.appliedRollEffect)} → {day.movement.effectiveMoveCount}マス</dd></div>}
            {day.movement?.eventType && day.movement.eventType !== 'none' && <div><dt>停止イベント</dt><dd>{day.movement.eventMessage ?? day.movement.eventType}</dd></div>}
            {day.movement?.extraRollValue != null && <div><dt>追加サイコロ</dt><dd>{day.movement.extraRollValue}（さらに＋{day.movement.extraRollValue}マス）</dd></div>}
            <div><dt>すごろく</dt><dd>{day.movement ? `${day.movement.fromPosition} → ${day.movement.toPosition}（実際${day.movement.appliedMoveCount}マス）` : '—'}</dd></div><div><dt>周回</dt><dd>{day.movement ? `${day.movement.lapNumber}周目・${day.movement.boardGoal ?? 30}マス盤` : '—'}</dd></div></dl>
          {day.photoSubmission && <p className="historyPhoto">📷 写真提出 {day.photoSubmission.submissionCount}回・{day.photoSubmission.finalStatus === 'approved' ? 'OK' : day.photoSubmission.finalStatus === 'retry_requested' ? 'やり直し' : day.photoSubmission.finalStatus === 'expired' ? '保存期限切れ' : '確認待ち'}</p>}
          {day.rewardTicket && <p className="historyReward">🎟️ ごほうび：<strong>{day.rewardTicket.rewardName}</strong></p>}
          {day.status === 'approved' && <div className="historyRestart">
            {day.restart.allowed ? <>
              <p>承認を取り消して、この日の課題を最初からやり直せます。</p>
              <button type="button" onClick={() => setRestartTarget(day)}>この日の学習をやり直す</button>
            </> : day.restart.reason === 'treasure_opened' ?
              <p className="historyRestartBlocked">宝箱を開けた後は学習をやり直せません。</p> : null}
          </div>}
        </>}
      </div>
    </details>)}</div>
    {restartTarget && history && <div className="confirmOverlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !restartBusy) setRestartTarget(null)
    }}>
      <section className="confirmDialog" role="dialog" aria-modal="true" aria-labelledby="restart-dialog-title">
        <p className="eyebrow">{history.child.name}・{displayDate(restartTarget.studyDate)}</p>
        <h2 id="restart-dialog-title">この日の学習をやり直しますか？</h2>
        <p>課題の完了状態と現在の保護者承認を取り消し、最初から学習できる状態に戻します。</p>
        <p className="confirmWarning">削除済みの学習写真は復元されません。</p>
        <div className="confirmActions">
          <button type="button" disabled={restartBusy} onClick={() => setRestartTarget(null)}>キャンセル</button>
          <button type="button" className="dangerButton" disabled={restartBusy} onClick={() => void restartStudy()}>{restartBusy ? '戻しています…' : '学習をやり直す'}</button>
        </div>
      </section>
    </div>}
  </div>
}

function ParentHome({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [study, setStudy] = useState<ParentStudy | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [view, setView] = useState<ParentView>('dashboard')
  const [selectedTodayChildId, setSelectedTodayChildId] = useState('')

  const load = async () => {
    setError('')
    try {
      const result = await api<ParentStudy>('/api/parent/study/today')
      setStudy(result)
      setSelectedTodayChildId((current) => current || result.children[0]?.child.id || '')
    } catch (caught) { setError(messageForError(caught)) }
  }
  useEffect(() => { if (view === 'today') void load() }, [view])

  const addTask = async (childStudy: DailyStudy) => {
    const childId = childStudy.child.id
    const taskName = drafts[childId]?.trim()
    if (!taskName) return
    const confirmApprovedChange = childStudy.status === 'approved'
      ? window.confirm('この日は承認済みです。承認履歴を残したまま、課題を追加して再確認待ちに戻しますか？')
      : false
    if (childStudy.status === 'approved' && !confirmApprovedChange) return
    setBusy(`add-${childId}`)
    try {
      await api(`/api/parent/study/children/${encodeURIComponent(childId)}/tasks`, {
        method: 'POST', body: JSON.stringify({ taskName, confirmApprovedChange }),
      })
      setDrafts((current) => ({ ...current, [childId]: '' }))
      await load()
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  const editTask = async (childStudy: DailyStudy, task: DailyTask) => {
    const childId = childStudy.child.id
    const taskName = window.prompt('勉強内容を変更', task.taskName)?.trim()
    if (!taskName || taskName === task.taskName) return
    const confirmApprovedChange = childStudy.status === 'approved'
      ? window.confirm('この日は承認済みです。完了・承認履歴を残したまま変更し、再確認待ちに戻しますか？')
      : false
    if (childStudy.status === 'approved' && !confirmApprovedChange) return
    const confirmCompletedChange = confirmApprovedChange || (task.completedAt
      ? window.confirm('この課題は完了済みです。完了した記録を残したまま、新しい未完了課題として変更しますか？')
      : false)
    if (task.completedAt && !confirmCompletedChange) return
    setBusy(task.id)
    try {
      await api(`/api/parent/study/children/${encodeURIComponent(childId)}/tasks/${encodeURIComponent(task.id)}`, {
        method: 'PATCH', body: JSON.stringify({ taskName, confirmCompletedChange, confirmApprovedChange }),
      })
      await load()
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  const deleteTask = async (childStudy: DailyStudy, task: DailyTask) => {
    const childId = childStudy.child.id
    const message = childStudy.status === 'approved'
      ? 'この日は承認済みです。完了・承認履歴を残したまま一覧から外し、再確認待ちに戻しますか？'
      : task.completedAt
      ? '完了済みです。完了記録を残したまま、今日の一覧から外しますか？'
      : 'この課題を今日の一覧から外しますか？'
    if (!window.confirm(message)) return
    setBusy(task.id)
    try {
      await api(`/api/parent/study/children/${encodeURIComponent(childId)}/tasks/${encodeURIComponent(task.id)}`, {
        method: 'DELETE', body: JSON.stringify({
          confirmCompletedChange: Boolean(task.completedAt),
          confirmApprovedChange: childStudy.status === 'approved',
        }),
      })
      await load()
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  const approve = async (childStudy: DailyStudy) => {
    if (!window.confirm(`${childStudy.child.name}の今日の勉強をOKにしますか？`)) return
    setBusy(`approve-${childStudy.child.id}`)
    setError('')
    try {
      await api(`/api/parent/study/children/${encodeURIComponent(childStudy.child.id)}/approve`, {
        method: 'POST', body: '{}',
      })
      await load()
    } catch (caught) { setError(messageForError(caught)) } finally { setBusy('') }
  }

  return (
    <main className="shell parentShell">
      <Brand />
      <div className="sessionBar"><span><b>{user.name}</b>・保護者画面</span><button onClick={onLogout}>ログアウト</button></div>
      <section className="parentCard">
        <nav className="parentViews" aria-label="保護者メニュー">
          <button className={view === 'dashboard' ? 'selected' : ''} aria-current={view === 'dashboard' ? 'page' : undefined} onClick={() => setView('dashboard')}>ホーム</button>
          <button className={view === 'today' ? 'selected' : ''} aria-current={view === 'today' ? 'page' : undefined} onClick={() => setView('today')}>今日だけ変更</button>
          <button className={view === 'photos' ? 'selected' : ''} aria-current={view === 'photos' ? 'page' : undefined} onClick={() => setView('photos')}>写真</button>
          <button className={view === 'schedule' ? 'selected' : ''} aria-current={view === 'schedule' ? 'page' : undefined} onClick={() => setView('schedule')}>通常予定</button>
          <button className={view === 'rewards' ? 'selected' : ''} aria-current={view === 'rewards' ? 'page' : undefined} onClick={() => setView('rewards')}>ごほうび</button>
          <button className={view === 'history' ? 'selected' : ''} aria-current={view === 'history' ? 'page' : undefined} onClick={() => setView('history')}>履歴</button>
          <button className={view === 'settings' ? 'selected' : ''} aria-current={view === 'settings' ? 'page' : undefined} onClick={() => setView('settings')}>設定</button>
        </nav>
        {view === 'dashboard' ? <ParentDashboard onNavigate={setView} /> : view === 'schedule' ? <ScheduleEditor /> : view === 'rewards' ? <RewardEditor /> : view === 'history' ? <HistoryView /> : view === 'settings' ? <ParentSettings /> : view === 'photos' ? <PhotoManager /> : <>
        <div className="parentHeading"><div><p className="eyebrow">今日の勉強設定</p><h1>それぞれのクエストを決める</h1></div>{study && <time>{displayDate(study.studyDate)}</time>}</div>
        <p className="lead">今日だけの内容を子どもごとに設定できます。完了済みの内容を変えても、過去の完了記録は残ります。</p>
        {error && <p className="error" role="alert">{error}</p>}
        {!study && !error && <p className="loadingLine">家族の予定を確認中…</p>}
        {study && <div className="childTabs todayChildTabs" aria-label="確認する子どもを選ぶ">
          {study.children.map((childStudy) => <button key={childStudy.child.id} className={selectedTodayChildId === childStudy.child.id ? 'selected' : ''} aria-pressed={selectedTodayChildId === childStudy.child.id} onClick={() => setSelectedTodayChildId(childStudy.child.id)}>{childStudy.child.name}</button>)}
        </div>}
        <div className="parentChildren">
          {study?.children.filter((item) => item.child.id === selectedTodayChildId).map((childStudy) => (
            <section className={`childPlan ${childStudy.status}`} key={childStudy.child.id}>
              <div className="childPlanHeader"><div><span className="childAvatar">★</span><h2>{childStudy.child.name}</h2></div><strong>{childStudy.progress.total}つ中 {childStudy.progress.completed}つ完了</strong></div>
              <p className="parentBoardPosition">すごろく <strong>{childStudy.board.currentPosition} / {childStudy.board.goal}マス</strong>{childStudy.board.reachedGoal && <span>GOAL!</span>}</p>
              <div className={`approvalStatus ${childStudy.status}`}>
                {childStudy.isRestDay ? <><strong>☀ お休み</strong><span>今日は休んでOKです</span></>
                  : childStudy.status === 'approved' ? <><strong>✓ 承認済み</strong><span>今日の学習は正式完了です</span></>
                  : childStudy.photoSubmissionState === 'pending' ? <><strong>📷 写真確認待ち</strong><span>写真画面から確認してください</span></>
                  : childStudy.photoSubmissionState === 'required' || childStudy.photoSubmissionState === 'retry_requested' ? <><strong>📸 写真提出待ち</strong><span>子どもが写真を送るのを待っています</span></>
                  : childStudy.status === 'pending' ? <><strong>● 承認待ち</strong><span>すべての課題が完了しています</span></>
                    : <><strong>学習中</strong><span>未完了の課題があります</span></>}
              </div>
              <p className={`sourceNotice ${childStudy.taskSource}`}>{childStudy.taskSource === 'override' ? '今日だけ変更されています' : childStudy.taskSource === 'schedule' ? '通常予定から表示中' : '今日の予定は未設定です'}</p>
              <div className="parentTaskList">
                {childStudy.tasks.length === 0 && <p className="noTasks">まだ課題がありません</p>}
                {childStudy.tasks.map((task) => (
                  <div className={`parentTask ${task.completedAt ? 'done' : ''}`} key={task.id}>
                    <span>{task.completedAt ? '✓ 完了' : '未完了'}</span><strong>{task.taskName}</strong>
                    <div><button disabled={busy === task.id} onClick={() => editTask(childStudy, task)}>編集</button><button className="deleteButton" disabled={busy === task.id} onClick={() => deleteTask(childStudy, task)}>削除</button></div>
                  </div>
                ))}
              </div>
              {childStudy.status === 'pending' && !childStudy.photoSubmissionEnabled && <button className="approveButton" disabled={busy === `approve-${childStudy.child.id}`} onClick={() => approve(childStudy)}>{busy === `approve-${childStudy.child.id}` ? '承認中…' : '今日の勉強をOKにする'}</button>}
              {childStudy.status === 'approved' && <p className="approvedEditNote">変更すると承認履歴を残したまま、再確認が必要な状態へ戻ります。</p>}
              <form className="addTaskForm" onSubmit={(event) => { event.preventDefault(); void addTask(childStudy) }}>
                <label><span>課題を追加</span><input value={drafts[childStudy.child.id] ?? ''} maxLength={100} placeholder="例：漢字ドリル 12〜13ページ" onChange={(event) => setDrafts((current) => ({ ...current, [childStudy.child.id]: event.target.value }))} /></label>
                <button disabled={busy === `add-${childStudy.child.id}` || !drafts[childStudy.child.id]?.trim()}>追加する</button>
              </form>
            </section>
          ))}
        </div>
        </>}
      </section>
    </main>
  )
}

function PrivacyPage() {
  return (
    <main className="privacyShell">
      <article className="privacyCard">
        <h1>おうちクエスト<br />プライバシーポリシー</h1>
        <p className="privacyUpdated">制定日：2026年9月13日</p>
        <p>おうちクエストは、特定の家族が家庭内で学習記録を管理するためのWebアプリです。取得する情報を必要最小限にし、次の方針で取り扱います。</p>
        <h2>取得・保存する情報</h2>
        <ul>
          <li>家族が設定した表示名、権限、PINの安全なハッシュ、ログインセッション</li>
          <li>学習予定、完了・承認履歴、すごろく、ごほうび券などの利用記録</li>
          <li>提出された学習写真と、その管理に必要な最小限のメタデータ</li>
        </ul>
        <h2>利用目的</h2>
        <p>毎日の学習課題の表示、保護者による確認、すごろくとごほうび機能、および安全なログイン状態の維持にのみ利用します。</p>
        <h2>学習写真</h2>
        <p>学習写真は非公開のGoogle Drive専用フォルダへ一時保存し、公開共有URLにはしません。保護者はおうちクエストの認証済み画面を通してのみ確認できます。写真は保護者のOKまたは再提出依頼後に削除し、未確認の場合も保存期限（現在30日）を過ぎると削除対象になります。データベースには画像本体を保存しません。</p>
        <h2>外部サービス</h2>
        <p>アプリの提供とデータ保存にCloudflare Pages、Workers、D1を利用し、学習写真の一時保存にGoogle Drive APIを利用します。Google Driveへのアクセスは、このアプリが作成・利用するファイルに限定した権限を使用します。</p>
        <h2>第三者提供</h2>
        <p>法令上必要な場合を除き、保存した情報を家族外の第三者へ販売または提供しません。</p>
        <h2>削除・問い合わせ</h2>
        <p>保存情報の確認や削除を希望する場合は、このアプリを管理する保護者へお問い合わせください。Googleアカウント側から、おうちクエストに付与したアクセス権をいつでも取り消せます。</p>
        <p><a href="/">おうちクエストへ戻る</a></p>
      </article>
    </main>
  )
}

function QuestApplication() {
  const [loading, setLoading] = useState(true)
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    Promise.all([
      api<Bootstrap>('/api/auth/bootstrap'),
      api<{ user: User | null }>('/api/auth/session'),
    ]).then(([initial, session]) => {
      setBootstrap(initial)
      setUser(session.user)
    }).catch((error) => setLoadError(messageForError(error))).finally(() => setLoading(false))
  }, [])

  const logout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST', body: '{}' })
    } finally {
      setUser(null)
      try { setBootstrap(await api<Bootstrap>('/api/auth/bootstrap')) } catch { /* keep the login screen usable */ }
    }
  }

  if (loading) return <main className="shell loading"><Brand /><p>おうちクエストを準備中…</p></main>
  if (loadError || !bootstrap) return <main className="shell loading"><Brand /><p className="error">{loadError}</p><button className="primaryButton" onClick={() => location.reload()}>もう一度読み込む</button></main>
  if (user?.role === 'parent') return <ParentHome user={user} onLogout={logout} />
  if (user?.role === 'child') return <ChildHome user={user} onLogout={logout} />
  if (bootstrap.setupRequired) return <SetupScreen onComplete={(nextUser) => { setUser(nextUser); setBootstrap((current) => current ? { ...current, setupRequired: false } : current) }} />
  return <LoginScreen users={bootstrap.users} onLogin={setUser} />
}

export function App() {
  return location.pathname === '/privacy' || location.pathname === '/privacy.html'
    ? <PrivacyPage />
    : <QuestApplication />
}
